import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";

import { DeskSelect } from "@/components/itsm-config/desk-select";
import {
  ConditionsCell,
  ExportRuleConditionFields,
} from "@/components/itsm-config/export-rule-conditions";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { TeamField, TeamLine } from "@/components/itsm-config/team-select";
import { useDraft } from "@/components/itsm-config/draft-context";
import { useCatalogItemNames } from "@/hooks/use-catalog-item-names";
import { useItsmCredentials } from "@/hooks/use-itsm-config";
import { useDeskName, useCredentialName } from "@/hooks/use-itsm-labels";
import {
  pmEndpoints,
  pmHasWorkspaces,
  pmVocabulary,
  useMondayColumns,
  usePmLaneName,
  usePmLanes,
  usePmScopeName,
  usePmScopes,
  usePmWorkspaces,
} from "@/hooks/use-pm-metadata";
import {
  EMPTY_CONDITIONS,
  conditionFormShape,
  conditionsMatch,
  conditionsToForm,
  hasConditionValues,
  type ConditionValues,
} from "@/lib/itsm-config/export-rule-conditions";
import { teamMatch } from "@/lib/itsm-config/export-rule-team";
import {
  EMPTY_MATCH,
  isEmptyMatch,
  upsertRuleField,
} from "@/lib/itsm-config/general-mapping";
import {
  PROVIDER_LABELS,
  type PmExportMappingRule,
  type ProviderConfig,
} from "@/lib/types/itsm-config";

/** Row cells that resolve ids → names (hooks can't run inside .map()). */
function DeskCell({
  tenantId,
  deskId,
}: {
  tenantId: string;
  deskId?: string | null;
}) {
  const name = useDeskName(tenantId, deskId);
  return <>{name || "— any —"}</>;
}

function ScopeCell({
  provider,
  tenantId,
  scopeId,
  workspaceId,
}: {
  provider: string;
  tenantId: string;
  scopeId?: string | null;
  workspaceId?: string | null;
}) {
  const name = usePmScopeName(provider, tenantId, scopeId, workspaceId);
  return <>{name || "—"}</>;
}

function LaneCell({
  provider,
  tenantId,
  scopeId,
  laneId,
}: {
  provider: string;
  tenantId: string;
  scopeId?: string | null;
  laneId?: string | null;
}) {
  const name = usePmLaneName(provider, tenantId, scopeId, laneId);
  return <>{name || "— default —"}</>;
}

function CredentialCell({
  tenantId,
  credentialId,
}: {
  tenantId: string;
  credentialId?: string | null;
}) {
  const name = useCredentialName(tenantId, credentialId);
  return <>{name || "— all —"}</>;
}

const formSchema = z
  .object({
    desk_id: z.string().trim().optional(),
    subdesk_id: z.string().trim().optional(),
    ...conditionFormShape,
    integration_id: z.string().trim().optional(),
    provider_scope: z.string().trim().optional(),
    provider_lane: z.string().trim().optional(),
  })
  .refine((v) => v.exclude || Boolean(v.provider_scope), {
    path: ["provider_scope"],
    message: "Pick a destination",
  })
  .refine(hasConditionValues, {
    path: ["custom_field_values"],
    message: "Add at least one value",
  });
type FormValues = z.infer<typeof formSchema>;

const EMPTY_FORM: FormValues = {
  ...EMPTY_CONDITIONS,
  provider_scope: "",
};

interface Props {
  tenantId: string;
  provider: string;
  providerConfig: ProviderConfig;
  /** The credential being edited. The PM metadata endpoints resolve the
   *  tenant's own credential rather than taking one, so it only pins
   *  "Don't sync" rules (see onSubmit). */
  credentialId?: string;
}

/**
 * Export rules for a project-management provider.
 *
 * Deliberately separate from `ExportRulesSection`: PM providers create a task
 * and never sync updates, so issue types, excluded fields, priority/status
 * translation and inbound settings have nothing to configure. What they add is
 * a lane inside the scope (an Asana section, a monday group).
 *
 * The card also owns the auto-create switch. The backend gate is
 * `export_mapping` non-empty AND `outbound_sync` true on some general_mapping
 * rule — rules alone create nothing, so the toggle lives next to what it gates
 * rather than in a section of its own.
 */
export function PmExportRulesSection({
  tenantId,
  provider,
  providerConfig,
  credentialId,
}: Props) {
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;
  // The draft carries the shared ExportMappingRule type; PM rules add the lane.
  const rules = (providerConfig.export_mapping ?? []) as PmExportMappingRule[];
  // -1 = add; >=0 = edit that index; null = closed.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const { update } = useDraft();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
  });

  const { pinsWorkspace } = pmEndpoints(provider);
  const hasWorkspaces = pmHasWorkspaces(provider);
  const vocab = pmVocabulary(provider);
  const anItem = `${/^[aeiou]/i.test(vocab.item) ? "an" : "a"} ${vocab.item}`;

  // Where the selected workspace lives depends on the provider. Asana pins it
  // into the config: one credential can reach several, and a project gid does
  // not say which holds it, so task creation has to be told. That choice sits
  // on the card, because changing it discards the rules.
  //
  // monday stores nothing — a board id is unique account-wide — so its
  // workspace is only a filter for the board picker and lives in the rule
  // dialog beside it. Reopening a rule starts on the workspace that holds its
  // board, so the list offers the board the rule already names: monday's
  // unscoped board list covers a single workspace and often would not.
  //
  // Unset, for Asana, means "the credential's first", which is what config
  // written before the workspace was selectable implies; the response echoes
  // back whichever answered, so the picker still shows it.
  const [localWorkspace, setLocalWorkspace] = useState<string | null>(null);
  const editedRule =
    // eslint-disable-next-line security/detect-object-injection -- editIndex is a validated array index
    editIndex !== null && editIndex >= 0 ? rules[editIndex] : undefined;
  const editedBoard = useMondayColumns(
    tenantId,
    hasWorkspaces && !pinsWorkspace
      ? (editedRule?.provider_scope ?? undefined)
      : undefined,
  );
  const workspaceId = pinsWorkspace
    ? (providerConfig.workspace_gid ?? undefined)
    : (localWorkspace ?? editedBoard.data?.workspace_id ?? undefined);

  const workspaces = usePmWorkspaces(provider, tenantId);
  const scopes = usePmScopes(provider, tenantId, workspaceId);
  const [pendingWorkspace, setPendingWorkspace] = useState<string | null>(null);
  // Lanes cascade off the scope chosen in the dialog.
  const selectedScope = form.watch("provider_scope");
  const excluded = form.watch("exclude");
  const selectedDesk = form.watch("desk_id");

  const catalog = useCatalogItemNames(tenantId);
  const lanes = usePmLanes(provider, tenantId, selectedScope || undefined);

  const { data: credentials } = useItsmCredentials(tenantId);
  const credentialOptions = (credentials ?? [])
    .filter((c) => c.provider === provider)
    .map((c) => ({ value: c.id, label: `${c.name} (${c.provider})` }));

  const autoCreate = (providerConfig.general_mapping ?? []).some(
    (r) => r.outbound_sync,
  );

  /**
   * Flip auto-create on the catch-all (empty-match) general_mapping rule.
   *
   * Addressed by its match rather than its position: monday also keeps a
   * board-scoped rule per board for its status mapping, so the first rule is
   * not necessarily the catch-all. The backend reads the switch as "set on
   * any rule", so it is cleared from every other rule.
   */
  const setAutoCreate = (enabled: boolean) => {
    update((current) => {
      const others = (current.general_mapping ?? []).map((r) =>
        isEmptyMatch(r.match) ? r : { ...r, outbound_sync: null },
      );
      return {
        general_mapping: upsertRuleField(others, EMPTY_MATCH, {
          outbound_sync: enabled,
        }),
      };
    });
  };

  const openAdd = () => {
    form.reset(EMPTY_FORM);
    setLocalWorkspace(null);
    setEditIndex(-1);
  };
  const openEdit = (i: number) => {
    // eslint-disable-next-line security/detect-object-injection -- i is a numeric array index from a rendered row
    const rule = rules[i];
    form.reset({
      desk_id: rule.match.desk_id ?? "",
      subdesk_id: rule.match.subdesk_id ?? "",
      ...conditionsToForm(rule),
      integration_id: rule.match.integration_id ?? "",
      provider_scope: rule.provider_scope ?? "",
      provider_lane: rule.provider_lane ?? "",
    });
    // Falls through to the workspace holding this rule's board.
    setLocalWorkspace(null);
    setEditIndex(i);
  };

  /** Writes the rule list into the page draft; persisted on the page Save. */
  const persist = (nextRules: PmExportMappingRule[]) => {
    update({
      export_mapping: nextRules.length ? nextRules : null,
      // Pin the workspace alongside the rules, for the providers that need
      // it: one Asana credential can expose several, and task creation must
      // not guess which one holds the project. Falls back to whichever the
      // scope list resolved, so a config saved without ever touching the
      // picker still records one. monday stores nothing here — its board id
      // already identifies the destination on its own.
      ...(pinsWorkspace
        ? workspaceId
          ? { workspace_gid: workspaceId }
          : scopes.data?.workspace_gid
            ? { workspace_gid: scopes.data.workspace_gid }
            : {}
        : {}),
    });
  };

  /**
   * Switch workspace, discarding rules that pointed into the old one.
   *
   * Only reachable for a provider that pins the workspace. There, scopes are
   * per-workspace, so every existing `provider_scope` becomes an id this
   * workspace does not contain; keeping them would leave rules that look
   * configured and route nowhere, so the change is confirmed first when
   * there is anything to lose.
   */
  const applyWorkspace = (nextWorkspace: string) => {
    update({ workspace_gid: nextWorkspace, export_mapping: null });
    setPendingWorkspace(null);
  };

  const onWorkspaceChange = (next: string) => {
    if (!next || next === workspaceId) return;
    // Nothing is invalidated when the workspace is not part of the config:
    // a monday board id stays valid whichever workspace the list was filtered
    // by, so this is a filter change, not a destructive one.
    if (!pinsWorkspace) {
      setLocalWorkspace(next);
      return;
    }
    if (rules.length === 0) {
      update({ workspace_gid: next });
      return;
    }
    setPendingWorkspace(next);
  };

  const onSubmit = (values: FormValues) => {
    // Spread the rule being edited so fields this form doesn't own survive.
    const original =
      // eslint-disable-next-line security/detect-object-injection -- editIndex is a validated array index
      editIndex !== null && editIndex >= 0 ? rules[editIndex] : undefined;
    const rule: PmExportMappingRule = {
      ...original,
      match: {
        ...original?.match,
        desk_id: values.desk_id || null,
        subdesk_id: teamMatch(values.desk_id, values.subdesk_id),
        ...conditionsMatch(values),
        // A rule bound to a credential outranks any unbound rule, so an
        // exclusion left unbound could never beat a bound include. Pin it
        // to the credential being edited unless the author chose one.
        integration_id:
          values.integration_id ||
          (values.exclude ? credentialId : null) ||
          null,
      },
      // An excluded ticket goes nowhere, so a destination would only mislead.
      provider_scope: values.exclude ? null : values.provider_scope || null,
      provider_lane: values.exclude ? null : values.provider_lane || null,
      exclude: values.exclude || null,
    };
    const next =
      editIndex === -1
        ? [...rules, rule]
        : rules.map((r, i) => (i === editIndex ? rule : r));
    persist(next);
    setEditIndex(null);
    form.reset(EMPTY_FORM);
  };

  const onDelete = () => {
    if (deleteIndex === null) return;
    persist(rules.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Where tickets go</CardTitle>
          <CardDescription>
            When a ticket is created on a matched desk, Harmony creates {anItem}{" "}
            in the mapped {providerLabel} {vocab.scope}, linked on the ticket as
            a sub-ticket. Narrow a rule to a team, catalog items or a custom
            field value, or mark it &quot;Don&apos;t sync&quot; to keep matching
            tickets out; the most specific rule wins.{" "}
            {providerConfig.two_way_sync
              ? `Two-way sync is on, so the ${vocab.item} then stays in step ` +
                "with the ticket in both directions."
              : `Updates are never synced back — the ${vocab.item} is a ` +
                "one-time handoff."}
          </CardDescription>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add rule
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasWorkspaces && pinsWorkspace && (
          <div className="space-y-2 rounded-md border px-4 py-3">
            <p className="text-sm font-medium">Workspace</p>
            <p className="text-muted-foreground text-sm">
              {`${vocab.scopeTitle}s below come from this workspace. Change ` +
                "it only if the desks should route into a different one — " +
                "the rules are cleared, because their " +
                `${vocab.scope}s do not exist outside it.`}
            </p>
            <MetadataSelect
              options={workspaces.data?.options ?? []}
              value={workspaceId ?? scopes.data?.workspace_gid ?? ""}
              onChange={onWorkspaceChange}
              isLoading={workspaces.isLoading}
              placeholder="Select a workspace"
              allowCustom={false}
              className="max-w-md"
            />
          </div>
        )}

        <div className="flex items-start gap-3 rounded-md border px-4 py-3">
          <div className="flex-1 space-y-0.5">
            <p className="text-sm font-medium">
              Create {vocab.item}s automatically
            </p>
            <p className="text-muted-foreground text-sm">
              {autoCreate
                ? `New tickets on a matched desk get ${anItem} without an agent ` +
                  "exporting them. Agents can still export manually."
                : "Off — the rules below are saved but nothing is created " +
                  "until an agent exports a ticket by hand."}
            </p>
          </div>
          <Switch checked={autoCreate} onCheckedChange={setAutoCreate} />
        </div>

        {rules.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            No export rules. Add one to route a desk to a {vocab.scope}.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Desk</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>{vocab.scopeTitle}</TableHead>
                <TableHead>{vocab.laneTitle}</TableHead>
                <TableHead>Credential</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule, i) => (
                <TableRow
                  // eslint-disable-next-line react-x/no-array-index-key -- config rules have no stable id
                  key={`${rule.match.desk_id ?? ""}-${rule.provider_scope ?? ""}-${i}`}
                  className="cursor-pointer"
                  onClick={() => {
                    openEdit(i);
                  }}
                >
                  <TableCell>
                    <DeskCell tenantId={tenantId} deskId={rule.match.desk_id} />
                    <TeamLine
                      tenantId={tenantId}
                      deskId={rule.match.desk_id}
                      subdeskId={rule.match.subdesk_id}
                    />
                  </TableCell>
                  <TableCell>
                    <ConditionsCell rule={rule} catalogNames={catalog.names} />
                  </TableCell>
                  {rule.exclude ? (
                    <TableCell colSpan={2}>
                      <span className="text-destructive font-medium">
                        Don&apos;t sync
                      </span>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <ScopeCell
                          provider={provider}
                          tenantId={tenantId}
                          scopeId={rule.provider_scope}
                          // Only a pinned workspace scopes the lookup; a
                          // monday board is named by id whatever the dialog
                          // last showed.
                          workspaceId={pinsWorkspace ? workspaceId : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        <LaneCell
                          provider={provider}
                          tenantId={tenantId}
                          scopeId={rule.provider_scope}
                          laneId={rule.provider_lane}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-muted-foreground text-xs">
                    <CredentialCell
                      tenantId={tenantId}
                      credentialId={rule.match.integration_id}
                    />
                  </TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setDeleteIndex(i);
                      }}
                    >
                      <Trash2 className="text-destructive h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add / edit dialog */}
      <Dialog
        open={editIndex !== null}
        onOpenChange={(o) => {
          if (!o) setEditIndex(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editIndex === -1 ? "Add export rule" : "Edit export rule"}
            </DialogTitle>
            <DialogDescription>
              A ticket on the matched desk becomes {anItem} in the given{" "}
              {vocab.scope}. Leave a match field blank to match anything.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                void form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
              id="pm-export-rule-form"
            >
              <FormField
                control={form.control}
                name="desk_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Harmony desk</FormLabel>
                    <FormControl>
                      <DeskSelect
                        tenantId={tenantId}
                        value={field.value ?? ""}
                        onChange={(v) => {
                          field.onChange(v);
                          // Teams belong to the desk they were picked from.
                          if (v !== field.value)
                            form.setValue("subdesk_id", "");
                        }}
                        placeholder="Select a desk (blank = all desks)"
                      />
                    </FormControl>
                    <FormDescription>
                      Which desk this rule routes. Leave unset to match all
                      desks.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TeamField
                form={form as unknown as UseFormReturn<{ subdesk_id?: string }>}
                tenantId={tenantId}
                deskId={selectedDesk || undefined}
              />
              <ExportRuleConditionFields
                form={form as unknown as UseFormReturn<ConditionValues>}
                tenantId={tenantId}
                deskId={selectedDesk || undefined}
                excludeDescription={
                  `Tickets this rule matches get no ${vocab.item}. It wins ` +
                  "over rules that are equally or less specific; a more " +
                  `specific rule that names a ${vocab.scope} still wins.`
                }
              />
              {!excluded && hasWorkspaces && !pinsWorkspace && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Workspace</p>
                  <MetadataSelect
                    options={workspaces.data?.options ?? []}
                    value={workspaceId ?? ""}
                    onChange={onWorkspaceChange}
                    isLoading={workspaces.isLoading || editedBoard.isLoading}
                    placeholder="Select a workspace"
                    allowCustom={false}
                  />
                  <p className="text-muted-foreground text-sm">
                    Narrows the {vocab.scope} list below and is not saved on the
                    rule — a {vocab.scope} keeps its identity whichever
                    workspace you browse.
                  </p>
                </div>
              )}
              {!excluded && (
                <>
                  <FormField
                    control={form.control}
                    name="provider_scope"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{vocab.scopeTitle}</FormLabel>
                        <FormControl>
                          <MetadataSelect
                            options={scopes.data?.options ?? []}
                            isLoading={scopes.isLoading}
                            value={field.value ?? ""}
                            onChange={(v) => {
                              field.onChange(v);
                              // The old lane belongs to the old scope.
                              form.setValue("provider_lane", "");
                            }}
                            placeholder={`Select a ${vocab.scope}...`}
                          />
                        </FormControl>
                        <FormDescription>
                          The {providerLabel} {vocab.scope} tickets on the
                          matched desk are created in.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="provider_lane"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{vocab.laneTitle} (optional)</FormLabel>
                        <FormControl>
                          <MetadataSelect
                            options={lanes.data?.options ?? []}
                            isLoading={lanes.isLoading}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            disabled={!selectedScope}
                            placeholder={
                              selectedScope
                                ? `${vocab.scopeTitle} default`
                                : `Pick a ${vocab.scope} first`
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Where in the {vocab.scope} the {vocab.item} lands.
                          Leave unset to use the {vocab.scope}&apos;s default{" "}
                          {vocab.lane}.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
              <FormField
                control={form.control}
                name="integration_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credential id (optional)</FormLabel>
                    <FormControl>
                      <MetadataSelect
                        options={credentialOptions}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        placeholder="All credentials"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditIndex(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" form="pm-export-rule-form">
              {editIndex === -1 ? "Add rule" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteIndex(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete export rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the rule from the provider config. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingWorkspace !== null}
        onOpenChange={(o) => {
          if (!o) setPendingWorkspace(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              {rules.length === 1
                ? `The existing export rule points at a ${vocab.scope} in the `
                : `The ${String(rules.length)} existing export rules point at ${vocab.scope}s in the `}
              current workspace, which do not exist in the new one. They will be
              cleared and you will need to map the desks again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingWorkspace) applyWorkspace(pendingWorkspace);
              }}
            >
              Change and clear rules
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
